import cx from 'classnames';
import {
  findNodePath,
  PlateElement,
  PlateElementProps,
  selectEditor,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import {
  Resizable,
  ResizableProvider,
} from 'lib-knowledge-document-structure/plate/resizable';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyInlineImageElement,
  MyValue,
} from 'lib-knowledge-document-structure/plateTypes';
import { useSelected } from 'lib-knowledge-document-structure/slate/react';
import { imageURLWithEditorWidth } from 'lib-knowledge-management-common/hooks/useUploadImage';
import { forwardRef } from 'react';

import { ResizeHandle } from '../resizeHandle';
import resizeStyles from '../resizeHandle/resizeHandle.module.scss';

import s from './elements.module.scss';

const InlineImageElement = forwardRef<
  React.ElementRef<typeof PlateElement>,
  PlateElementProps<MyValue, MyInlineImageElement>
>(({ className, children, ...props }, ref) => {
  const editor = useMyEditorRef();
  const isSelected = useSelected();
  const { readOnly } = useEditableProps();
  const path = findNodePath(editor, props.element);

  const onClickImage = () => {
    selectEditor(editor, {
      at: path,
    });
  };

  return (
    <PlateElement asChild ref={ref} className={className} {...props}>
      <span
        className={cx(
          {
            [s.selected]: isSelected,
            [resizeStyles['resizeContainerHoverFocused']]: isSelected,
          },
          s.inlineImage,
          resizeStyles['resizeContainer'],
        )}
      >
        <ResizableProvider>
          <Resizable
            options={{
              readOnly: readOnly,
              align: 'left',
              minWidth: 16,
              maxWidth: 640,
            }}
          >
            <ResizeHandleLeft readOnly={readOnly} />
            <img
              src={imageURLWithEditorWidth(
                props.element.url,
                props.element.originalWidth,
              )}
              onClick={onClickImage}
            />
            <ResizeHandleRight readOnly={readOnly} />
          </Resizable>
          {children}
        </ResizableProvider>
      </span>
    </PlateElement>
  );
});

function ResizeHandleLeft({ readOnly }: { readOnly: boolean | undefined }) {
  if (readOnly) return <></>;
  return <ResizeHandle side="left" closeHandle />;
}

function ResizeHandleRight({ readOnly }: { readOnly: boolean | undefined }) {
  if (readOnly) return <></>;
  return <ResizeHandle side="right" closeHandle />;
}

InlineImageElement.displayName = 'InlineImageElement';
export { InlineImageElement };
